import { Button, FileButton, Group, Loader, Stack, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCamera, IconPhoto } from '@tabler/icons-react'
import { fileUrl } from '@tnn/core'
import { InitialsAvatar } from '@tnn/ui'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import type { CustomFieldProps } from '../resource/config'
import classes from './photo.module.css'
import { squarePhoto } from './square'

export interface PhotoPickerProps {
  /** The photo's file id, or null */
  value: string | null
  /** Whose face: their initial until there is a photo */
  name: string
  /** Sends the photo (already made small and square) → its file id */
  upload: (file: File) => Promise<{ id: string }>
  onChange: (fileId: string | null) => void
  /** Where a saved photo shows from; a public page has none (it shows what was just taken, or what it kept of it) */
  srcOf?: ((fileId: string) => string | undefined) | null
  label?: ReactNode
  error?: ReactNode
  required?: boolean
  readOnly?: boolean
  size?: number
}

/**
 * A face photo: taken with the phone's front camera, or chosen from its pictures (a file on a computer), made small and
 * square before it is sent, then shown round as it will be on the person's avatar
 */
export function PhotoPicker({
  value,
  name,
  upload,
  onChange,
  srcOf = fileUrl,
  label,
  error,
  required,
  readOnly,
  size = 96,
}: PhotoPickerProps) {
  const { t } = useTranslation()
  const phone = useMediaQuery('(max-width: 47.99em)')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [taken, setTaken] = useState<string | null>(null)
  useEffect(
    () => () => {
      if (taken) URL.revokeObjectURL(taken)
    },
    [taken],
  )
  const take = async (file: File | null) => {
    if (!file) return
    setBusy(true)
    setFailed(null)
    try {
      const photo = await squarePhoto(file)
      const stored = await upload(photo)
      setTaken(URL.createObjectURL(photo))
      onChange(stored.id)
    } catch (e) {
      setFailed(e instanceof Error && e.message !== 'canvas' && e.message !== 'jpeg' ? e.message : t('photo.unreadable'))
    } finally {
      setBusy(false)
    }
  }
  const src = (value && taken) || (value && srcOf ? srcOf(value) : undefined)
  const problem = failed ?? error
  return (
    <Group gap="md" wrap="nowrap" align="center">
      <div className={classes.frame} data-error={!!problem || undefined}>
        <InitialsAvatar name={name || '?'} size={size} src={src} alt="" />
        {busy && (
          <div className={classes.busy}>
            <Loader size="sm" type="dots" color="white" />
          </div>
        )}
      </div>
      <Stack gap={6} miw={0}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
            {required && <span className={classes.required}> *</span>}
          </Text>
        )}
        {!readOnly && (
          <Group gap="xs">
            {phone && (
              <FileButton accept="image/*" capture="user" onChange={take} disabled={busy}>
                {(props) => (
                  <Button {...props} variant="light" size="compact-md" leftSection={<IconCamera size={18} />}>
                    {t('photo.take')}
                  </Button>
                )}
              </FileButton>
            )}
            <FileButton accept="image/*" onChange={take} disabled={busy}>
              {(props) => (
                <Button {...props} variant={phone ? 'subtle' : 'light'} size="compact-md" leftSection={<IconPhoto size={18} />}>
                  {t(phone ? 'photo.fromLibrary' : value ? 'photo.change' : 'photo.choose')}
                </Button>
              )}
            </FileButton>
          </Group>
        )}
        <Text size="xs" c={problem ? 'var(--tnn-error)' : 'dimmed'}>
          {problem ?? t('photo.hint')}
        </Text>
      </Stack>
    </Group>
  )
}

/** A resource form's photo field (`custom: true`): uploaded as the person filling it in, shown as it will be */
export function PhotoField({ form, field, readOnly, t }: CustomFieldProps) {
  const input = form.getInputProps(field.name)
  return (
    <PhotoPicker
      value={(input.value as string | null) ?? null}
      name={String(form.values.name ?? '')}
      upload={api.upload}
      onChange={(id) => form.setFieldValue(field.name, id)}
      label={t(field.label)}
      error={input.error}
      required={field.required && !readOnly}
      readOnly={readOnly}
    />
  )
}
